<script setup>
// 20년의 경험 섹션 — 반응형으로 옮긴 여섯 번째 섹션.
//
// 원본: Figma 홈(ver1) 4133:20504 (1920x1396), 배경 #fdfbf8
//   머리말 (가운데 정렬)
//     눈썹  y 150  15px semibold, 테두리 #a66e08, 알약   (아래 간격 20)
//     제목  y 212  56px bold 행간 66                      (아래 간격 30)
//     설명  y 308  20px 행간 28 투명도 50%, 2줄           (아래 간격 40)
//     버튼  y 404  42 높이 두 개, 간격 10                 (아래 간격 80)
//   카드 줄 y 526  x 64~1856 (1792), 282x620 여섯 장, 간격 20
//   아래 여백 250
//
// 원본에서 바꾼 것 — 머리말만이다.
//   절대좌표를 걷어내고 가운데 정렬 상자에 담았다. 글자는 전부 원본 값 고정이고
//   제목만 clamp 로 감쌌다(1280 이상 원본 56px 고정).
//
// 카드 줄의 연출은 손대지 않았다. 6장이 오른쪽에서 날아와 가운데에 676px 정사각형으로
// 겹치고, 282 로 변형된 뒤 펼쳐지는 순서가 좌표에 물려 있다. 그래서 1920 좌표 그대로 둔
// 그림 층에 넣고 통째로 줄인다.
//
// 카드 줄은 **언제나 한 줄**이다(2026-09-23 사용자 결정).
//   한때 좁은 창에서 3장씩 두 줄로 접었는데, 맥북(1713)에서 두 줄로 보이는 게
//   의도와 달랐다. 맥북은 최대로 줄여도 1168 이고 그 폭까지 한 줄이어야 한다.
//   그래서 접지 않고 줄 전체를 폭에 맞춰 줄인다.
//
//   원본 줄은 282 여섯 장 + 간격 20 다섯 = 1792 다. 좌우 여백 20 씩까지
//   1832 이상이면 배율 1 — 카드가 원본 282px 그대로다.
//   그보다 좁으면 (창폭 - 40) / 1792 로 줄인다. 카드 안 글자도 같이 작아진다.
//   캔버스 폭(1920)으로 나누던 예전보다는 크다 — 좌우 빈 자리를 세지 않기 때문이다.
//
// 배경은 제 것을 갖지 않는다(bare) — 섹션 뒤에 깔린 디졸브 배경이 색을 맡는다.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { HEADER_REAL } from '@/composables/useFitHeight.js'
import {
  SCENE_REVEAL_DELAY_MS,
  TRANSITION_MS,
  TRUST_CARD_ENTER_X,
  TRUST_CARD_FINAL_W,
  TRUST_CARD_OFFSETS,
  TRUST_CARD_SQUARE_W,
  TRUST_CARD_STAGGER_MS,
  TRUST_GATHER_PAUSE_MS,
  SCENE_TEXT_STAGGER_MS,
  TRUST_MORPH_MS,
  TRUST_SPREAD_MS,
  TRUST_STACK_CLIP_RIGHT,
  sceneTextReveal,
} from '@/data/homeMotionTiming.js'
import imgRectangle240649549 from '@/assets/figma/f44aca7b7820971c9afbb6843eb786f05db039c5.png'
import imgRectangle240649547 from '@/assets/figma/7259bb94e4a6c4b6e6637409570604f6b460028a.png'
import img from '@/assets/figma/8a23ac0340f2b5bd3f4c63a4c690bb0fc9a860e7.png'
import imgRectangle240649550 from '@/assets/figma/7060e3f3bb72e4ffa0611376198b643c0bc328c1.png'
import img1 from '@/assets/figma/9cd1a31e3101c6ca0ef18b2d7f63824c8d01c208.png'
import imgImage239 from '@/assets/figma/95fb05be94eef00605e2f1b80c11faefc176d2e3.png'
import img2 from '@/assets/figma/b9e892da96edf051ca749baffaf93067d700b2cf.png'
import imgRectangle240649548 from '@/assets/figma/84c89be3f003476cc8b3f1f4198c46eaccb16f92.png'
import imgGroup2147228376 from '@/assets/figma/f64a7fe0720b39c5ca97454463b9d69fcd990e0c.svg'
import imgCalendarMinus from '@/assets/figma/74b2fb18a60ec03e3e7b7d6243435ca7f81d0dd6.svg'
import imgGroup2117905670 from '@/assets/figma/f33bce990ca21d5f21f4991eb7aaf87f5310294b.svg'
import imgGroup2147228381 from '@/assets/figma/7114b9fb583b3ae1b394a0c4aa000bb2b1687d9e.svg'
import imgGroup2147228114 from '@/assets/figma/4334fda66526080b047701f4c20b51a02b0289c6.svg'
import imgGroup2117905546 from '@/assets/figma/ce758decb7c2faf9d9463ba477e511651d0b1f29.svg'

const props = defineProps({
  // 현재 스토리보드 프레임 번호 (홈1-N)
  step: { type: Number, default: 29 },
  // 배경을 끄고 나온다.
  bare: { type: Boolean, default: false },
})

const FIRST_FRAME = 21 // 카드 시퀀스가 시작되는 스텝

// stage: 0 대기 / 1~6 카드 n장 모임 / 7 직사각형 변형 / 8 최종 위치
const stage = ref(0)
let timers = []

const clearTimers = () => {
  timers.forEach(clearTimeout)
  timers = []
}

const runSequence = () => {
  clearTimers()
  // 1 = 첫 카드가 와 있는 상태. 그 카드는 이미 서 있으므로 여기서 시작한다.
  // 예전처럼 0 에서 시작하면 첫 칸이 빈 박자가 되어 다음 카드가 한 박 늦는다.
  stage.value = 1
  // 앞의 뜸은 두지 않는다. 글은 이미 4.7 칸 앞(headReady)에서 떴고, 여기까지
  // 왔다는 건 카드 하단까지 스크롤됐다는 뜻이라 바로 돈다.
  let t = 0
  for (let i = 2; i <= 6; i += 1) {
    timers.push(setTimeout(() => (stage.value = i), t))
    t += TRUST_CARD_STAGGER_MS
  }
  t += TRUST_GATHER_PAUSE_MS
  timers.push(setTimeout(() => (stage.value = 7), t))
  t += TRUST_MORPH_MS
  timers.push(setTimeout(() => (stage.value = 8), t))
}

// 섹션에 들어왔는가. 첫 카드는 이 순간 제자리에 나타나 기다린다.
const entered = computed(() => props.step >= FIRST_FRAME)

// 머리말이 아래에서 올라오며 드러나는 역커튼 (2026-09-26 요청).
//
// 이 섹션만 머리말에 연출이 없어 처음부터 떠 있었다. 다른 다섯 섹션과 같은 모양으로
// 맞춘다 - 거래소개의 것과 같은 식이다.
//
// 카드 줄과 켜지는 때가 다르다. 머리말은 섹션에 들어오면 바로 올라오고,
// 카드는 카드 줄이 화면에 다 들어와야 날아온다(rowReady).
// 두 단계뿐이다 - 0 은 눈썹+제목, 1 은 설명과 그 아래.
const curtain = (order) => sceneTextReveal(headReady.value, order)

// ── 카드 줄이 다 보이는가 ────────────────────────────────────────────
//
// 고정 헤더가 위를 가린다(HEADER_H, 설계 px). 그 아래부터 창 바닥까지가 볼 수 있는 자리다.
// getBoundingClientRect 는 실제 px 로 나오므로 헤더도 실제 px 로 바꿔 견준다 —
// 화면 뿌리가 --z 로 제 배율을 내려 준다.
//
// 한 번 참이면 되돌리지 않는다. 프레임과 같은 약속이다(HomeSceneView 의 lift) —
// 위로 올렸다 다시 내려도 연출이 처음부터 다시 돌지 않는다.
// 머리말이 화면에 다 들어왔는가. 글과 첫 카드가 이때 뜬다(2026-09-27 요청).
//
// 카드 시퀀스(entered)보다 한참 앞이다 - 1366x768 에서 582 대 1085 로 4.7 칸 차이다.
// 섹션 프레임에 묶어 두면 둘이 같은 순간이 되어, 스크롤을 내려도 빈 화면이다가
// 글과 카드가 같이 튀어나온다.
// ★ 이 시점은 2026-10-01 에 고정으로 정했다. 바꾸지 말 것.
//
//   다른 네 섹션은 '글 아랫변이 창 바닥에 닿을 때' 켜지는데 여기만 다르다.
//   1440x731 에서 머리말 675, 같은 규칙이면 1150 - 475 차이다.
//   "텍스트 효과가 안 맞는다" 는 제보로 맞출지 검토했으나, 맞추면 글과 카드가
//   같이 쏟아져 2026-09-27 에 떼어 낸 이유가 되살아난다.
//   사용자 판단: "신뢰섹션의 인터렉션의 시작은 지금을 고정으로. 다시 바꾸면 안 된다."
const headEl = ref(null)
const headReady = ref(false)

const checkHead = () => {
  if (headReady.value || !headEl.value) return
  const r = headEl.value.getBoundingClientRect()
  // 아랫변이 창 안에 들어오면 켠다. "지나갔는가" 라 한 번 참이면 계속 참이다.
  if (r.bottom <= window.innerHeight) headReady.value = true
}

const rowEl = ref(null)
const rowReady = ref(false)

// 머리말(headReady)보다 먼저 켜질 걱정은 없다. 머리말 덩어리가 줄 위에 있으니
// 아랫변도 늘 먼저 창에 들어온다 - 1366x768 에서 582 대 1007 이다.
// 한때 entered(섹션 한가운데)로 막아 뒀는데, 그러면 78px 늦어져 카드 하단이
// 온전히 보이고 제목이 헤더 밑으로 사라진 뒤에야 돌았다(2026-09-27 제보).
const checkRow = () => {
  if (rowReady.value || !rowEl.value) return
  const z = parseFloat(getComputedStyle(rowEl.value).getPropertyValue('--z')) || 1
  // 헤더는 배율 밖이라 실제 px 그대로다(2026-10-15).
  const head = HEADER_REAL
  const r = rowEl.value.getBoundingClientRect()
  // 창이 낮아 줄이 통째로 들어가지 못하면 영영 안 돌게 된다.
  // 그때는 줄 윗변이 헤더 밑까지 올라온 순간, 즉 보일 수 있는 최대일 때 시작한다.
  // 아랫변이 조금 걸쳐 있어도 켠다. 딱 맞게 보길 기다리면 8px 차이로 한 칸을
  // 더 내려야 하고, 그 사이 스크롤이 지나가 버린다(2026-09-26 제보).
  const slack = r.height * 0.1
  const roomy = r.height <= window.innerHeight - head + slack
  // "지나갔는가" 로 본다. 예전에는 윗변과 아랫변을 같이 봐서(구간 조건) 빠르게 내리면
  // 그 사이를 건너뛰고 카드가 영영 안 돌았다 - entered 까지 겹치면 창이 200px,
  // 스크롤 한 칸이 100px 이라 두 칸이면 지나친다(2026-09-27).
  // 아랫변만 보면 한 번 참이 된 뒤로 계속 참이라 놓칠 수가 없다.
  const shown = roomy ? r.bottom <= window.innerHeight + slack : r.top <= head + 1
  if (shown) rowReady.value = true
}

watch(rowReady, (ready) => {
  if (ready) runSequence()
})

// 섹션에서 벗어나면(위로 크게 올리면) 처음 상태로 돌린다.
watch(entered, (active) => {
  if (active) return
  clearTimers()
  stage.value = 0
})

// 모이는 동안에는 카드 묶음의 오른쪽 끝에서 잘라둔다.
// 변형(stage 7)부터는 펼쳐져야 하므로 클립을 푼다.
const stackClip = computed(() => ({
  clipPath: stage.value >= 7 ? 'none' : `inset(0 ${TRUST_STACK_CLIP_RIGHT}px 0 0)`,
}))

// order: 왼쪽부터 0~5. 등장 순서와 최종 위치 순서가 같다.
const cardStyle = (order) => {
  const st = stage.value
  // 첫 카드는 섹션에 들어온 순간부터 서 있다. 나머지는 제 차례에 도착한다.
  const arrived = order === 0 ? headReady.value : st >= order + 1
  const morphed = st >= 7
  const spread = st >= 8
  // 첫 카드(거래액)는 이동하지 않는다. 스토리보드 홈1-19/20/21 모두 x=622 로 고정이고
  // 제자리에서 나타난다. 나머지 5장만 첫 카드 오른쪽(x=1307)에서 대기하다 들어온다.
  const entryX = order === 0 ? 0 : TRUST_CARD_ENTER_X
  const x = spread ? TRUST_CARD_OFFSETS[order] : arrived ? 0 : entryX
  return {
    left: `calc(50% + ${x}px)`,
    width: `${morphed ? TRUST_CARD_FINAL_W : TRUST_CARD_SQUARE_W}px`,
    opacity: arrived ? 1 : 0,
    zIndex: order,
    transitionProperty: 'left, width, opacity',
    transitionDuration: spread
      ? `${TRUST_SPREAD_MS}ms`
      : morphed
        ? `${TRUST_MORPH_MS}ms`
        : `${TRUST_CARD_STAGGER_MS + 250}ms`,
    transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
  }
}


// ── 그림 층 배율 ─────────────────────────────────────────────────────
//
// 카드 여섯 장이 언제나 한 줄이다. 줄이 들어갈 만큼 줄여서 넣는다.
//   줄 폭 1792 = 282x6 + 간격 20x5, 좌우 여백 20 씩 → 1832 이상이면 배율 1
// 상한은 1 이다. 1832 를 넘어도 더 키우지 않는다.
// 머리말 아래 버튼("회사소개 바로가기" · "IR 바로가기")을 보일 것인가.
// 2026-09-25 에 감춰 달라는 요청으로 껐다. 자리가 비면서 카드 줄이 그만큼 위로 온다.
const SHOW_CTA = false

const DESIGN_W = 1920
const CARDS_H = 620
const ROW6_W = 1792
const GUTTER = 40

const root = ref(null)
const artScale = ref(1)
let observer = null

const measureArt = () => {
  const w = root.value?.clientWidth
  if (!w) return
  // 배율은 화면 뿌리(HomeSceneView)의 zoom 이 전담한다. 여기서 또 줄이면 두 번 줄어든다.
  artScale.value = 1
}

// ResizeObserver 와 window.resize 를 같이 듣는다.
// 요소 폭이 안 바뀌는데 창만 바뀌는 경우(세로만 줄이기, 탭이 가려져 있다가 돌아오기)가 있고,
// 그때 ResizeObserver 는 울리지 않는다. 그러면 한 줄/두 줄 판정이 옛 값에 머문다.
onMounted(() => {
  measureArt()
  observer = new ResizeObserver(measureArt)
  observer.observe(root.value)
  window.addEventListener('resize', measureArt)
  // 카드 줄이 다 보이는 순간을 잡는다. 스크롤과 창 크기 둘 다에 걸린다.
  checkHead()
  checkRow()
  window.addEventListener('scroll', checkHead, { passive: true })
  window.addEventListener('resize', checkHead)
  window.addEventListener('scroll', checkRow, { passive: true })
  window.addEventListener('resize', checkRow)
})
onBeforeUnmount(() => {
  clearTimers()
  observer?.disconnect()
  observer = null
  window.removeEventListener('resize', measureArt)
  window.removeEventListener('scroll', checkHead)
  window.removeEventListener('resize', checkHead)
  window.removeEventListener('scroll', checkRow)
  window.removeEventListener('resize', checkRow)
})
</script>

<template>
  <section
    ref="root"
    class="relative flex w-full flex-col justify-center overflow-hidden px-[20px]"
    :class="{ 'bg-[#fdfbf8]': !bare }"
    :style="{ '--r': artScale }"
    data-node-id="4133:20504"
  >
    <!-- 머리말. 반응형이고 글자는 원본 값 그대로다. -->
    <div
      ref="headEl"
      class="mx-auto flex w-full max-w-[1190px] flex-col items-center text-center"
    >
      <span class="trust-eyebrow" :style="curtain(0)">Built on Trust. Proven by Scale</span>

      <h2 class="trust-title mt-[30px]" :style="curtain(0)">
        20년의 경험, 숫자로 증명된 신뢰
      </h2>

      <!-- 한 줄로 둔다(2026-09-25 요청). 원본은 두 줄이었다. -->
      <p class="trust-sub mt-[30px]" :style="curtain(1)">
        거래 규모부터 콘텐츠 영향력, 전국 네트워크까지 골드나라가 쌓아온 시장의 깊이와 경쟁력을
        수치로 확인할 수 있습니다.
      </p>

      <!-- 버튼은 감춰 둔다(2026-09-25 요청). 이 자리가 비면서 아래 카드 줄이 위로 올라온다.
           다시 켜려면 SHOW_CTA 만 true 로 바꾸면 된다. -->
      <div
        v-if="SHOW_CTA"
        class="mt-[40px] flex flex-wrap justify-center gap-[10px]"
        :style="curtain(1)"
      >
        <span class="trust-cta trust-cta-dark">
          회사소개 바로가기
          <span class="trust-arrow"><img :src="imgGroup2117905546" alt="" /></span>
        </span>
        <span class="trust-cta trust-cta-gold">
          IR 바로가기
          <span class="trust-arrow"><img :src="imgGroup2117905546" alt="" /></span>
        </span>
      </div>
    </div>

    <!-- 카드 줄. 연출이 좌표에 물려 있어 1920 그대로 두고 통째로 줄인다.
         언제나 한 줄이다.
         윗여백은 원본이 80 이었다. 그 80 은 머리말 버튼 아래 간격이었는데 버튼을
         감추면서(SHOW_CTA) 설명글 바로 밑이 되어 넓어 보였다. 2026-09-26 에 40 으로
         좁혔다 - 섹션 내용이 900 에서 860 이 되어 줄임 배율이 그만큼 덜 걸린다. -->
    <div
      ref="rowEl"
      class="relative mt-[40px] w-full"
      :style="{ height: `${CARDS_H * artScale}px` }"
    >
      <div
        class="absolute left-1/2 top-0 origin-top"
        :style="{
          width: `${DESIGN_W}px`,
          height: `${CARDS_H}px`,
          transform: `translateX(-50%) scale(${artScale})`,
        }"
      >
        <div class="-translate-x-1/2 absolute h-[620px] left-1/2 top-0 w-[1792px]" :style="stackClip" data-node-id="4133:20506">
      <div class="-translate-x-1/2 absolute bg-[#f6f1e8] h-[620px] overflow-clip rounded-[30px] top-0 " :style="cardStyle(4)" data-node-id="4133:20507" data-name="유튜브, SNS 구독자">
            <div class="absolute h-[620px] right-0 rounded-[30px] top-0 w-full" data-node-id="4133:20508">
              <img alt="" class="absolute inset-0 max-w-none object-bottom pointer-events-none rounded-[30px] size-full" :src="imgRectangle240649549" />
            </div>
            <div class="absolute content-stretch flex flex-col gap-[45px] items-start left-[30px] top-[50px] w-[212px]" data-node-id="4133:20509">
              <div class="content-stretch flex flex-col gap-[10px] items-start relative shrink-0 w-full" data-node-id="4133:20510">
                <div class="content-stretch flex gap-[5px] items-start relative shrink-0" data-node-id="4133:20511">
                  <div class="overflow-clip relative shrink-0 size-[32px]" data-node-id="4133:20512" data-name="mynaui:user-solid">
                    <div class="absolute inset-[13.61%_16.51%_14.25%_16.54%]" data-node-id="4133:20513">
                      <img alt="" class="absolute block inset-0 max-w-none size-full" :src="imgGroup2147228376" />
                    </div>
                  </div>
                  <p class="[word-break:break-word] font-semibold leading-[32px] not-italic relative shrink-0 text-[#744e06] text-[24px] whitespace-nowrap" data-node-id="4133:20516">
                    유튜브, SNS 구독자
                  </p>
                </div>
                <p class="[word-break:break-word] font-bold leading-[60px] not-italic relative shrink-0 text-[50px] text-black tracking-[-1.5px] whitespace-nowrap" data-node-id="4133:20517">
                  100만 +
                </p>
              </div>
              <p class="[word-break:break-word] font-medium leading-[28px] not-italic relative shrink-0 text-[20px] text-black whitespace-nowrap" data-node-id="4133:20518">
                * 개인 채널 10만 +
              </p>
            </div>
            <div class="[word-break:break-word] absolute content-stretch flex flex-col gap-[30px] items-start justify-center left-[30px] not-italic text-black top-[316px]" data-node-id="4133:20519">
              <div class="content-stretch flex flex-col gap-[10px] items-start relative shrink-0" data-node-id="4133:20520">
                <p class="font-medium leading-[28px] opacity-50 relative shrink-0 text-[20px] whitespace-nowrap" data-node-id="4133:20521">
                  영상 누적 제작 콘텐츠
                </p>
                <p class="font-bold leading-[38px] min-w-full relative shrink-0 text-[30px] w-[min-content]" data-node-id="4133:20522">
                  3,700+
                </p>
              </div>
              <div class="content-stretch flex flex-col gap-[10px] items-start relative shrink-0" data-node-id="4133:20523">
                <p class="font-medium leading-[28px] opacity-50 relative shrink-0 text-[20px] whitespace-nowrap" data-node-id="4133:20524">
                  유튜브 누적 조회수
                </p>
                <p class="font-bold leading-[38px] min-w-full relative shrink-0 text-[30px] w-[min-content]" data-node-id="4133:20525">
                  1.35억+
                </p>
              </div>
            </div>
            <div class="absolute bg-black h-px left-[30px] opacity-10 top-[275px] w-[222px]" data-node-id="4133:20526" />
          </div>
      <div class="-translate-x-1/2 absolute bg-black h-[620px] overflow-clip rounded-[30px] top-0 " :style="cardStyle(0)" data-node-id="4133:20527" data-name="거래액">
            <div class="absolute h-[620px] left-0 rounded-[30px] top-0 w-full" data-node-id="4133:20528">
              <div class="absolute inset-0 overflow-hidden pointer-events-none rounded-[30px]">
                <img alt="" class="absolute h-[-169.58%] left-[129.79%] max-w-none top-[144.3%] w-[-248.85%]" :src="imgRectangle240649547" />
              </div>
            </div>
            <div class="absolute content-stretch flex flex-col gap-[45px] items-start left-[30px] top-[50px] w-[212px]" data-node-id="4133:20529">
              <div class="content-stretch flex flex-col gap-[10px] items-start relative shrink-0 w-full" data-node-id="4133:20530">
                <div class="content-stretch flex gap-[5px] items-start relative shrink-0" data-node-id="4133:20531">
                  <div class="overflow-clip relative shrink-0 size-[32px]" data-node-id="4133:20532" data-name="ChartBar">
                    <div class="absolute contents inset-[21.47%_18.41%_21.45%_18.43%]" data-node-id="4133:20533">
                      <div class="absolute bg-white inset-[21.47%_18.41%_21.45%_62.57%] rounded-[1.067px]" data-node-id="4133:20534" />
                      <div class="absolute bg-white inset-[37.77%_40.49%_21.46%_40.49%] rounded-[1.067px]" data-node-id="4133:20535" />
                      <div class="absolute bg-white inset-[54.09%_62.55%_21.45%_18.43%] rounded-[1.067px]" data-node-id="4133:20536" />
                    </div>
                  </div>
                  <p class="[word-break:break-word] font-semibold leading-[32px] not-italic relative shrink-0 text-[24px] text-white whitespace-nowrap" data-node-id="4133:20537">
                    거래액
                  </p>
                </div>
                <p class="[word-break:break-word] font-bold leading-[60px] not-italic relative shrink-0 text-[50px] text-white tracking-[-1.5px] whitespace-nowrap" data-node-id="4133:20538">
                  2,100억
                </p>
              </div>
              <p class="[word-break:break-word] font-medium leading-[28px] not-italic opacity-50 relative shrink-0 text-[20px] text-white whitespace-nowrap" data-node-id="4133:20539">
                2025년 기준
              </p>
            </div>
          </div>
      <div class="-translate-x-1/2 absolute h-[620px] overflow-clip rounded-[30px] top-0 " :style="cardStyle(3)" data-node-id="4133:20540" data-name="업력">
            <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none rounded-[30px] size-full" :src="img" />
            <div class="absolute content-stretch flex flex-col gap-[45px] items-start left-[30px] top-[50px] w-[212px]" data-node-id="4133:20541">
              <div class="content-stretch flex flex-col gap-[10px] items-start relative shrink-0 w-full" data-node-id="4133:20542">
                <div class="content-stretch flex gap-[5px] items-start relative shrink-0" data-node-id="4133:20543">
                  <div class="relative shrink-0 size-[32px]" data-node-id="4133:20544" data-name="CalendarMinus">
                    <img alt="" class="absolute block inset-0 max-w-none size-full" :src="imgCalendarMinus" />
                  </div>
                  <p class="[word-break:break-word] font-semibold leading-[32px] not-italic relative shrink-0 text-[#91681e] text-[24px] whitespace-nowrap" data-node-id="4133:20547">
                    업력
                  </p>
                </div>
                <p class="[word-break:break-word] font-bold leading-[60px] not-italic relative shrink-0 text-[50px] text-black tracking-[-1.5px] whitespace-nowrap" data-node-id="4133:20548">
                  20년
                </p>
              </div>
              <p class="[word-break:break-word] font-medium leading-[28px] not-italic opacity-50 relative shrink-0 text-[20px] text-black whitespace-nowrap" data-node-id="4133:20549">
                창업 이래
              </p>
            </div>
          </div>
      <div class="-translate-x-1/2 absolute bg-[#f6f1e8] h-[620px] overflow-clip rounded-[30px] top-0 " :style="cardStyle(5)" data-node-id="4133:20550" data-name="시장규모">
            <div class="absolute bottom-0 h-[620px] right-0 rounded-[30px] w-full" data-node-id="4133:20551">
              <div aria-hidden class="absolute inset-0 pointer-events-none rounded-[30px]">
                <img alt="" class="absolute max-w-none object-bottom rounded-[30px] size-full" :src="imgRectangle240649550" />
                <div class="absolute bg-[rgba(0,0,0,0.2)] inset-0 rounded-[30px]" />
              </div>
            </div>
            <div class="absolute content-stretch flex flex-col gap-[45px] items-start left-[30px] top-[50px] w-[212px]" data-node-id="4133:20552">
              <div class="content-stretch flex flex-col gap-[10px] items-start relative shrink-0 w-full" data-node-id="4133:20553">
                <div class="content-stretch flex gap-[5px] items-start relative shrink-0" data-node-id="4133:20554">
                  <div class="overflow-clip relative shrink-0 size-[32px]" data-node-id="4133:20555" data-name="CalendarMinus">
                    <div class="absolute inset-[16.98%_16.96%_16.99%_17%]" data-node-id="4133:20556">
                      <div class="absolute inset-[-6.31%]">
                        <img alt="" class="block max-w-none size-full" :src="imgGroup2117905670" />
                      </div>
                    </div>
                  </div>
                  <p class="[word-break:break-word] font-semibold leading-[32px] not-italic relative shrink-0 text-[24px] text-white whitespace-nowrap" data-node-id="4133:20560">
                    시장규모
                  </p>
                </div>
                <p class="[word-break:break-word] font-bold leading-[60px] not-italic relative shrink-0 text-[50px] text-white tracking-[-1.5px] whitespace-nowrap" data-node-id="4133:20561">
                  20조
                </p>
              </div>
              <div class="[word-break:break-word] font-medium leading-[0] not-italic opacity-50 relative shrink-0 text-[20px] text-white whitespace-nowrap" data-node-id="4133:20562">
                <p class="leading-[28px] mb-0 whitespace-pre">{{ '25년 실물 기준 ' }}</p>
                <p class="leading-[28px] whitespace-pre">국내 시장</p>
              </div>
            </div>
          </div>
      <div class="-translate-x-1/2 absolute h-[620px] overflow-clip rounded-[30px] top-0 " :style="cardStyle(1)" data-node-id="4133:20563" data-name="회원사">
            <div aria-hidden class="absolute inset-0 pointer-events-none rounded-[30px]">
              <img alt="" class="absolute max-w-none object-cover rounded-[30px] size-full" :src="img1" />
              <div class="absolute bg-[#3d3629] inset-0 mix-blend-hard-light rounded-[30px]" />
            </div>
            <div class="absolute content-stretch flex flex-col gap-[45px] items-start left-[30px] top-[50px] w-[212px]" data-node-id="4133:20564">
              <div class="content-stretch flex flex-col gap-[10px] items-start relative shrink-0 w-full" data-node-id="4133:20565">
                <div class="content-stretch flex gap-[5px] items-start relative shrink-0" data-node-id="4133:20566">
                  <div class="relative shrink-0 size-[32px]" data-node-id="4133:20567">
                    <div class="absolute h-[20.866px] left-[8.38px] top-[5.57px] w-[15.25px]" data-node-id="4133:20568">
                      <img alt="" class="absolute block inset-0 max-w-none size-full" :src="imgGroup2147228381" />
                    </div>
                  </div>
                  <p class="[word-break:break-word] font-semibold leading-[32px] not-italic relative shrink-0 text-[24px] text-white whitespace-nowrap" data-node-id="4133:20581">
                    회원사
                  </p>
                </div>
                <p class="[word-break:break-word] font-bold leading-[60px] not-italic relative shrink-0 text-[50px] text-white tracking-[-1.5px] whitespace-nowrap" data-node-id="4133:20582">
                  7,000개
                </p>
              </div>
              <p class="[word-break:break-word] font-medium leading-[28px] not-italic opacity-50 relative shrink-0 text-[20px] text-white whitespace-nowrap" data-node-id="4133:20583">
                한국귀금속판매업중앙회
              </p>
            </div>
            <div class="absolute left-[142px] opacity-90 size-[100px] top-[480px]" data-node-id="4133:20584" data-name="image 239">
              <div class="absolute inset-0 overflow-hidden pointer-events-none">
                <img alt="" class="absolute h-[254.15%] left-[-76.57%] max-w-none top-[-69.18%] w-[253.14%]" :src="imgImage239" />
              </div>
            </div>
          </div>
      <div class="-translate-x-1/2 absolute h-[620px] overflow-clip rounded-[30px] top-0 " :style="cardStyle(2)" data-node-id="4133:20585" data-name="전국 직영매장">
            <img alt="" class="absolute inset-0 max-w-none object-bottom pointer-events-none rounded-[30px] size-full" :src="img2" />
            <div class="absolute bottom-0 h-[367px] right-[-2px] rounded-bl-[30px] rounded-br-[30px] w-[284px]" data-node-id="4133:20586">
              <img alt="" class="absolute inset-0 max-w-none object-bottom pointer-events-none rounded-bl-[30px] rounded-br-[30px] size-full" :src="imgRectangle240649548" />
            </div>
            <div class="absolute content-stretch flex flex-col gap-[45px] items-start left-[30px] top-[50px] w-[212px]" data-node-id="4133:20587">
              <div class="content-stretch flex flex-col gap-[10px] items-start relative shrink-0 w-full" data-node-id="4133:20588">
                <div class="content-stretch flex gap-[5px] items-start relative shrink-0" data-node-id="4133:20589">
                  <div class="overflow-clip relative shrink-0 size-[32px]" data-node-id="4133:20590" data-name="CalendarMinus">
                    <div class="absolute inset-[16.5%_13.2%_16.52%_13.17%]" data-node-id="4133:20591">
                      <img alt="" class="absolute block inset-0 max-w-none size-full" :src="imgGroup2147228114" />
                    </div>
                  </div>
                  <p class="[word-break:break-word] font-semibold leading-[32px] not-italic relative shrink-0 text-[#744e06] text-[24px] whitespace-nowrap" data-node-id="4133:20596">
                    전국 직영매장
                  </p>
                </div>
                <p class="[word-break:break-word] font-bold leading-[60px] not-italic relative shrink-0 text-[50px] text-black tracking-[-1.5px] whitespace-nowrap" data-node-id="4133:20597">
                  6+
                </p>
              </div>
              <div class="[word-break:break-word] font-medium leading-[0] not-italic opacity-50 relative shrink-0 text-[20px] text-black whitespace-nowrap" data-node-id="4133:20598">
                <p class="leading-[28px] mb-0">서울, 부산</p>
                <p class="leading-[28px]">경남, 인천</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.trust-eyebrow {
  display: inline-flex;
  align-items: center;
  border: 1px solid #a66e08;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  color: #a66e08;
  white-space: nowrap;
}

/* 제목만 창 폭을 탄다. 1280 을 넘으면 원본 56px 에 걸려 더 커지지 않는다. */
/* 큰 숫자 줄도 제목과 같은 기준으로 줄인다.
   Tailwind 의 text-[50px] / leading-[60px] 로는 min() 을 쓸 수 없어 여기서 덮는다.
   scoped 는 .cls[data-v-x] 라 Tailwind 한 칸짜리 선택자를 이긴다. */
.text-\[50px\] {
  font-size: 50px;
}
.leading-\[60px\] {
  line-height: 60px;
}

.trust-title {
  /* 여섯 섹션 제목을 50 으로 맞췄다(2026-09-26 요청). 원본은 56 이었다.
     어느 해상도에서도 같은 크기라야 해서, 섹션이 통째로 줄어들 때(--fit)
     제목만 그만큼 되돌려 상쇄한다. */
  font-size: calc(50px / var(--fit, 1));
  line-height: 1.18;
  font-weight: 700;
  color: #000;
  word-break: keep-all;
}

.trust-sub {
  font-size: 20px;
  line-height: 28px;
  color: #000;
  opacity: 0.5;
  word-break: keep-all;
}

.trust-cta {
  display: inline-flex;
  align-items: center;
  gap: 11px;
  border-radius: 5px;
  padding: 10px 12px 10px 14px;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.16px;
  color: #fff;
  white-space: nowrap;
}
.trust-cta-dark {
  background-image: linear-gradient(106.353623617652deg, rgb(6, 5, 5) 3.4363%, rgb(208, 155, 0) 248.16%);
}
.trust-cta-gold {
  background-image: linear-gradient(to right, #bf9c51, #ba841e 127.25%);
}

.trust-arrow {
  display: block;
  width: 16px;
  height: 16px;
  transform: rotate(90deg);
}
.trust-arrow img {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
